import Button from "core/buttons/button/Button";
import {
  ACTIONS,
  ENTITIES,
  MODULES,
  QUERY_STATUS,
  PROFILE_ID,
} from "constants/constants";
import { FormattedMessage } from "react-intl";
import { HandleModaleBO } from "components/bo/HandleModaleBO";
import { TableManager } from "components/bo/admin-table/TableManager";
import { navigateEdit, navigateToParent, navigateView } from "utils/boHelper";
import { useBranchContext } from "api/settings/branch/BranchProviders";
import { useBranchesContext } from "api/settings/branches/BranchesProvider";
import { useCountryContext } from "api/settings/country/CountryProvider";
import { useEffect, useState } from "react";
import { useEvalTemplateContext } from "api/settings/eval-template/EvalTemplate";
import { useFileContext } from "api/files/FilesProvider";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { usePersonContext } from "api/directory/person/PersonProvider";
import { useRegionContext } from "api/settings/region/RegionProvider";
import { useTypeCompaniesContext } from "api/settings/type-companies/TypeCompaniesProvider";
import { useUserContext } from "api/user/UserProvider";
import { useStaffContext } from "api/directory/staff/StaffProvider";
import {
  branchForm,
  formatDisplayBranch,
  formatPayload,
  formatReceivedBranch,
} from "utils/branchHelper";
import { ROLE_ASSOC } from "access-roles/roles";
import { uploadFileHelper } from "utils/helper";

export default function AdminBranches({ action }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const {
    branchByIdGetter,
    branchDeleteById,
    branchList,
    branchListGetter,
    branchPerId,
    createBranch,
    updateBranch,
    createdBranch,
  } = useBranchContext();
  const { searchStaffByRoleAndName, staffList, staffListGetter } =
    useStaffContext();
  const {
    getPersonsByProfile,
    personsByProfileList,
    personsList,
    getPersonsByIds,
  } = usePersonContext();
  const { regionListGetter, regionList } = useRegionContext();
  const { fetchBranches, branches } = useBranchesContext();
  const { typeCompaniesListGetter, typeCompaniesList } =
    useTypeCompaniesContext();
  const { countryListGetter, countryList } = useCountryContext();
  const { getTemplatesList, templateList } = useEvalTemplateContext();
  const { lang } = useUserContext();

  const {
    createFile,
    createFileStatus,
    createdFile,
    setCreateFileStatus,
    setCreatedFile,
  } = useFileContext();
  const [data, setData] = useState();
  useEffect(() => {
    fetchBranches();
    regionListGetter();
    countryListGetter();
    getTemplatesList();
    typeCompaniesListGetter();
    staffListGetter(PROFILE_ID.ASSOC);
  }, [
    fetchBranches,
    regionListGetter,
    countryListGetter,
    getTemplatesList,
    typeCompaniesListGetter,
  ]);

  const [deleteData, setDeleteData] = useState(null);

  useEffect(() => {
    if (!id) branchListGetter();
  }, [branchListGetter]);

  useEffect(() => {
    if (id) {
      branchByIdGetter(id);
    }
  }, [id]);

  useEffect(() => {
    if (createdBranch?.id) {
      setCreatedFile({});
      navigateToParent(navigate, location);
    }
  }, [createdBranch]);

  const handleDelete = async (branchId) =>
    setDeleteData(branchList.find((e) => e.id === branchId));

  const handleCreate = (payload) => {
    const result = formatPayload(payload);
    setData(result);
    if (payload?.signature_file) {
      createFile({
        file: payload?.signature_file?.target?.value,
        module: MODULES.SETTINGS,
        alt: payload?.signature_file.target.files[0].name,
        fileName: payload?.signature_file.target.files[0].name,
      });
    } else {
      createBranch(result);
    }
  };

  const handleEdit = (payload) => {
    const result = formatPayload(payload);
    setData(result);
    if (payload?.signature_file !== branchPerId.signature_file) {
      const file = payload?.signature_file?.target?.files[0];
      uploadFileHelper({
        file,
        uploadQueryFunc: createFile,
        type: "signature-picture",
      });
    } else {
      updateBranch(payload.id, result);
      navigateToParent(navigate, location);
    }
  };

  useEffect(() => {
    if (createFileStatus === QUERY_STATUS.SUCCESS && createdFile) {
      if (id) {
        updateBranch(id, {
          ...data,
          signature_file: createdFile.uri,
        });
        navigateToParent(navigate, location);
      } else {
        createBranch({
          ...data,
          signature_file: createdFile.uri,
        });
      }
      setCreateFileStatus();
      setCreatedFile({});
    }
  }, [createFileStatus, createdFile]);

  useEffect(() => {
    if (branchPerId?.id) {
      getPersonsByIds({
        ids: `${branchPerId?.director_id},${
          branchPerId?.director_id
        },${branchPerId?.associates?.toString()},${branchPerId?.rgp_id}`,
      });
    }
  }, [branchPerId]);

  return (
    <div>
      {action === ACTIONS.LIST && (
        <div className="flex">
          <Button
            className="btn-hover-color"
            size="large"
            color="blue"
            onClick={() => navigateView("new", navigate, location)}
          >
            <FormattedMessage
              id={"admin_create_new"}
              defaultMessage={"Nouveau"}
            />
          </Button>
        </div>
      )}
      <br />
      <TableManager
        action={action}
        entity={ENTITIES.BRANCH}
        entityPerId={formatReceivedBranch({
          regionList,
          countryList,
          staffList,
          branch: branchPerId,
          branches,
          personsList,
          typeCompaniesList,
          lang,
        })}
        handleCreate={handleCreate}
        handleDelete={handleDelete}
        handleEdit={handleEdit}
        inputs={branchForm({
          staffList,
          branches,
          personsByProfileList,
          regionList,
          countryList,
          typeCompaniesList,
          templateList,
          lang,
        })}
        formattedEntity={formatDisplayBranch({
          countryList,
          regionList,
          staffList,
          branch: branchPerId,
          branches,
          personsList,
          typeCompaniesList,
          lang,
        })}
        list={branchList}
        navigateEdit={(propsId) => navigateEdit(propsId, navigate, location)}
        navigateView={(propsId) => navigateView(propsId, navigate, location)}
        search={(search) => {
          if (search && search.length >= 3)
            getPersonsByProfile({ profile: "rgp,gp,dsoc", search });
        }}
        searchRole={(search) => {
          if (search && search?.length >= 3)
            searchStaffByRoleAndName({ role: ROLE_ASSOC, search });
        }}
      />
      <div id="modalDelete">
        <HandleModaleBO
          setDeleteData={setDeleteData}
          validateDelete={branchDeleteById}
          deleteData={deleteData}
          entity={ENTITIES.BRANCH}
        />
      </div>
    </div>
  );
}
